<template>
  <div class="recommenders-admin" v-if="currentUser">
    <RecommendersModal
        :isVisible="isRecommendersModalVisible"
        @closeRecommendersModal="isRecommendersModalVisible = false"
        :recommenders="currentUsersRecommends"
    />

    <div class="page-header">
      <h1>Polecający</h1>
      <button class="btn-outline" @click="resetFilters">Resetuj filtry</button>
    </div>

    <div class="section-card">
      <div class="section-card-header">
        <h2>Wyszukiwanie</h2>

        <div class="filters-grid">
          <div class="form-group">
            <label for="filter">Szukaj po</label>
            <select name="filter" id="filter" v-model="searcher.choosedFilter">
              <option value="">Wybierz filtr</option>
              <option :value="item.value" v-for="(item, index) in searcher.filters" :key="index">
                {{ item.name }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label for="phrase">Fraza</label>
            <input
                id="phrase"
                type="text"
                v-model="searcher.searchPhrase"
                :disabled="!searcher.choosedFilter"
                :placeholder="searchPlaceholder"
                @keyup.enter="filterUsers"
            >
          </div>

          <div class="form-group">
            <label for="usersLimit">Pokaż na stronie</label>
            <select
                name="usersLimit"
                id="usersLimit"
                class="users-limit-select"
                v-model.number="usersList.limit"
                @change="onLimitChange"
            >
              <option :value="1">1</option>
              <option :value="10">10</option>
              <option :value="16">16</option>
              <option :value="22">22</option>
              <option :value="28">28</option>
              <option :value="34">34</option>
            </select>
          </div>
        </div>

        <div class="filters-actions">
          <button class="btn-green" @click="filterUsers">Szukaj</button>
          <button class="btn-outline" @click="resetFilters">Resetuj</button>
          <img v-if="searcher.loading" class="loading" src="@/assets/icons/loading.gif" alt="ładowanie">
        </div>
      </div>

      <div class="toolbar-row">
        <div class="results-chip">
          Wyniki: <b>{{ usersList.usersCount || 0 }}</b>
        </div>

        <div class="pagination">
          <button class="btn-outline" @click="prevPage" :disabled="usersList.page <= 1">
            &#8592; Poprzednia
          </button>
          <span class="page-info">Strona {{ usersList.page }}/{{ totalPages }}</span>
          <button class="btn-outline" @click="nextPage" :disabled="usersList.page >= totalPages">
            Następna &#8594;
          </button>
        </div>
      </div>
    </div>

    <div class="section-card table-card">
      <div class="section-card-header">
        <h2>Lista polecających</h2>
      </div>

      <div v-if="!usersList.usersList" class="empty-state">
        <p>Ładowanie danych...</p>
      </div>

      <div v-else-if="usersList.usersList.length === 0" class="empty-state">
        <p>Nie znaleziono polecających.</p>
      </div>

      <div v-else class="table-wrapper">
        <table class="recommenders-table">
          <thead>
          <tr>
            <th>ID</th>
            <th>Adres e-mail</th>
            <th>Ilość poleceń</th>
            <th>Akcje</th>
          </tr>
          </thead>
          <tbody>
          <tr v-for="user in usersList.usersList" :key="user.id">
            <td>{{ user.id }}</td>
            <td>
              <RouterLink class="email-link" :to="`/browse/user/${user.id}`">{{ user.email }}</RouterLink>
            </td>
            <td>
              <span class="count-chip">{{ user.recommends.length }}</span>
            </td>
            <td>
              <button
                  class="btn-primary"
                  @click="() => { isRecommendersModalVisible = true; currentUsersRecommends = user.recommends; }"
              >
                Pokaż
              </button>
            </td>
          </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import authHeader from '@/services/auth-header';
import axios from 'axios';
import RecommendersModal from '@/components/SpecialComponents/RecommendersModal.vue';
import { useAuthStore } from '@/stores/auth';

export default {
  name: 'RecommendersList',
  components: { RecommendersModal },
  setup() {
    return {
      authStore: useAuthStore(),
    };
  },
  data() {
    return {
      isRecommendersModalVisible: false,
      currentUsersRecommends: null,
      usersList: {
        usersList: null,
        page: 1,
        limit: 10,
        usersCount: null,
      },
      searcher: {
        searchPhrase: '',
        choosedFilter: '',
        loading: false,
        filters: [
          { name: 'adres e-mail', value: 'email' },
          { name: 'id', value: 'id' },
          { name: 'imię i nazwisko', value: 'name' },
        ],
      },
    };
  },
  computed: {
    currentUser() {
      return this.authStore.user;
    },
    totalPages() {
      const pages = Math.ceil((this.usersList.usersCount || 0) / this.usersList.limit);
      return pages > 0 ? pages : 1;
    },
    searchPlaceholder() {
      if (!this.searcher.choosedFilter) return 'Najpierw wybierz filtr';
      const found = this.searcher.filters.find(item => item.value === this.searcher.choosedFilter);
      return found ? found.name : 'Wpisz frazę';
    },
  },
  mounted() {
    if (!this.currentUser) {
      this.$router.push('/login');
    } else if (this.currentUser.role_id !== 1) {
      this.$router.push('/login');
    }
    this.getRecommenders();
  },
  methods: {
    getRecommenders() {
      this.searcher.loading = true;
      axios.post('/UsersWithRecommends', {
        page: this.usersList.page,
        limit: this.usersList.limit,
      }, authHeader()).then((res) => {
        this.usersList.usersCount = res.data.count;
        this.usersList.usersList = res.data.items;
      }).finally(() => {
        this.searcher.loading = false;
      });
    },
    filterUsers() {
      this.searcher.loading = true;
      const ob = {
        page: 1,
        limit: this.usersList.usersCount || this.usersList.limit,
      };

      axios.post('/UsersWithRecommends', ob, authHeader())
          .then(res => {
            let items = res.data.items || [];

            if (this.searcher.searchPhrase !== '') {
              switch (this.searcher.choosedFilter) {
                case 'email':
                  items = items.filter(user => user.email.toLowerCase().includes(this.searcher.searchPhrase.toLowerCase()));
                  break;
                case 'id':
                  items = items.filter(user => user.id == this.searcher.searchPhrase);
                  break;
                case 'name':
                  items = items.filter(user => `${user.firstname} ${user.lastname}`.toLowerCase().includes(this.searcher.searchPhrase.toLowerCase()));
                  break;
              }
            }

            this.usersList.usersList = items;
            this.usersList.usersCount = items.length;
            this.usersList.page = 1;
          })
          .finally(() => {
            this.searcher.loading = false;
          });
    },
    onLimitChange() {
      this.usersList.page = 1;
      this.getRecommenders();
    },
    prevPage() {
      if (this.usersList.page > 1) {
        this.usersList.page -= 1;
        this.getRecommenders();
      }
    },
    nextPage() {
      if (this.usersList.page < this.totalPages) {
        this.usersList.page += 1;
        this.getRecommenders();
      }
    },
    resetFilters() {
      this.searcher.searchPhrase = '';
      this.searcher.choosedFilter = '';
      this.usersList.page = 1;
      this.getRecommenders();
    },
  },
};
</script>

<style scoped lang="scss">
.recommenders-admin {
  max-width: 1200px;
  margin: 0 auto;
  padding: 32px 16px;
  font-family: sans-serif;
  box-sizing: border-box;
}

.page-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  margin-bottom: 24px;
  padding-bottom: 16px;
  border-bottom: 2px solid #e5e7eb;
}

.page-header h1 {
  font-size: 2.8rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.section-card {
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 1px 6px rgba(0, 0, 0, 0.08);
  padding: 24px;
  box-sizing: border-box;
  width: 100%;
  margin-bottom: 20px;
}

.section-card-header {
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 14px;
}

.section-card h2 {
  font-size: 2rem;
  font-weight: 700;
  color: #1f2937;
  margin: 0;
}

.filters-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(180px, 1fr));
  gap: 10px;
}

.form-group {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.form-group label {
  font-size: 1.4rem;
  font-weight: 600;
  color: #374151;
}

.form-group input,
.form-group select {
  border: 1px solid #d1d5db;
  border-radius: 8px;
  padding: 8px 12px;
  font-size: 1.4rem;
  outline: none;
  transition: border-color 0.2s;
  box-sizing: border-box;
  width: 100%;
}

.form-group input:focus,
.form-group select:focus {
  border-color: #10b981;
}

.filters-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}

.loading {
  width: 30px;
  height: 30px;
  object-fit: contain;
}

.toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
  padding-top: 8px;
  border-top: 1px solid #e5e7eb;
}

.results-chip {
  font-size: 1.4rem;
  color: #374151;
  background: #f3f4f6;
  border-radius: 999px;
  padding: 6px 12px;
}

.pagination {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.page-info {
  font-size: 1.5rem;
  color: #6b7280;
  font-weight: 500;
  min-width: 130px;
  text-align: center;
}

.table-card {
  margin-bottom: 0;
}

.table-wrapper {
  width: 100%;
  overflow-x: auto;
}

.recommenders-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  min-width: 760px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  overflow: hidden;
}

.recommenders-table thead th {
  background-color: #f9fafb;
  color: #374151;
  font-size: 1.4rem;
  font-weight: 700;
  text-align: left;
  padding: 12px;
  border-bottom: 1px solid #e5e7eb;
}

.recommenders-table tbody td {
  padding: 12px;
  font-size: 1.4rem;
  color: #1f2937;
  border-bottom: 1px solid #f1f5f9;
  vertical-align: middle;
}

.recommenders-table tbody tr:last-child td {
  border-bottom: none;
}

.recommenders-table tbody tr:hover td {
  background-color: #fcfcfd;
}

.email-link {
  color: #2563eb;
  text-decoration: none;
  word-break: break-word;
}

.email-link:hover {
  text-decoration: underline;
}

.count-chip {
  display: inline-block;
  background-color: #d1fae5;
  color: #065f46;
  font-size: 1.2rem;
  font-weight: 600;
  padding: 2px 10px;
  border-radius: 20px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}

.empty-state {
  text-align: center;
  padding: 28px;
  color: #9ca3af;
  font-size: 1.5rem;
  border: 1px dashed #d1d5db;
  border-radius: 10px;
}

.btn-primary {
  background-color: #f97316;
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  font-size: 1.35rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-primary:hover {
  background-color: #ea6c0a;
}

.btn-green {
  background-color: #10b981;
  color: #fff;
  padding: 8px 14px;
  border-radius: 8px;
  border: none;
  font-size: 1.35rem;
  font-weight: 600;
  cursor: pointer;
  transition: background-color 0.2s;
}

.btn-green:hover {
  background-color: #059669;
}

.btn-outline {
  background-color: transparent;
  color: #1f2937;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-size: 1.35rem;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s;
}

.btn-outline:hover {
  border-color: #10b981;
  color: #10b981;
}

button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

@media (max-width: 900px) {
  .filters-grid {
    grid-template-columns: 1fr;
  }

  .toolbar-row {
    flex-direction: column;
    align-items: stretch;
  }

  .pagination {
    justify-content: space-between;
  }
}

@media (max-width: 480px) {
  .recommenders-admin {
    padding: 16px 12px;
  }

  .page-header h1 {
    font-size: 2.2rem;
  }

  .section-card {
    padding: 16px 12px;
    border-radius: 8px;
  }

  .section-card h2 {
    font-size: 1.8rem;
  }

  .btn-primary,
  .btn-green,
  .btn-outline {
    width: 100%;
    text-align: center;
  }

  .filters-actions {
    width: 100%;
  }
}
</style>